import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import moment from "moment";
import { connect } from "react-redux";
import {
  Checkbox,
  Pagination,
  Card,
  Row,
  Col,
  Button,
  Modal,
  Tooltip,
  Grid,
} from "antd";
import {
  RollbackOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Image as ImageIcon,
  Film,
  Globe,
  ExternalLink,
  Radio,
  LayoutGrid,
  List as ListIcon,
  Play as PlayIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { getMediaTypeIcon } from "../../helpers/mediaTypeIcons";
import EmptyState from "../EmptyState";
import MediaListElement from "../../components/MediaListElement";
import MediaFilter from "../../components/views/MediaFilter";
import FolderList from "./FolderList";
import FolderBreadcrumb from "./FolderBreadcrumb";
import useFolderFileStore from "../../store/folderFileStore";
import { Container, Draggable } from "react-smooth-dnd";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import DraggableWrapper from "../../dnd/DraggableWarpper";
import { requestServerGet, requestServerPost } from "../../actions";
import useFileStore from "../../store/fileStore";
import { checkIsAPINew } from "../../Config";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";
// DragDropContext and Container imports are retained but logic is commented out

const MediaLists = (props) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).media;
  const {
    mediaDatas = [],
    type: propType = "",
    allowEdit: propAllowEdit = true,
    allowAssign: propAllowAssign = true,
    allowDelete: propAllowDelete = true,
    searchWord: propSearchWord = "",
    mode = "media",
    currentSelections = [],
    onPreview = () => {},
    onEdit = () => {},
    onDelete = () => {},
    onReplace = () => {},
    onRestore = () => {},
    onAssign = () => {},
    updateSelection = () => {},
    updateSelections = () => {},
    branches = [],
    // Space (in px) already occupied above this component by sticky
    // ancestors (top nav bar, and any page-level sticky header) — the
    // folder card and media list header stick right below that, instead
    // of a hardcoded guess, so they don't end up pinned underneath (and
    // cropped by) whatever is sticky above them.
    stickyOffset = 56,
  } = props;

  // --- STATE ---
  const [currentPage, setCurrentPage] = useState(props.currentPage || 1);
  const [pageSize, setPageSize] = useState(10);
  const [currentFilters, setCurrentFilters] = useState({});
  const [searchWord, setSearchWord] = useState(propSearchWord);
  const [type, setType] = useState(propType);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("media-view-mode") || "list",
  );
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        if (sortDir === "asc") {
          setSortDir("desc");
          return key;
        }
        // desc → clear
        setSortDir("asc");
        return null;
      }
      setSortDir("asc");
      return key;
    });
    setCurrentPage(1);
  };

  const SORT_FNS = {
    name: (d) => (d.FileName || "").toLowerCase(),
    type: (d) => (d.FileType || "").toLowerCase(),
    date: (d) => new Date(d.AddDate || 0).getTime(),
    usage: (d) => d.FileUseStatusID ?? 0,
    expiry: (d) => d.FileAvailableID ?? 0,
    deleted: (d) => new Date(d.DeleteDate || 0).getTime(),
  };
  const folderFileDatas = useFolderFileStore((state) => state.folderFileDatas);
  const selectParentFolder = useFolderFileStore(
    (state) => state.selectParentFolder,
  );
  const selectedFolderID = useFolderFileStore(
    (state) => state.selectedFolderID,
  );

  const fetchFolderFileDatas = useFolderFileStore(
    (state) => state.fetchFolderFileDatas,
  );
  const setParentFolderForTarget = useFolderFileStore(
    (state) => state.setParentFolderForTarget,
  );
  const setParentFolderForTargetFile = useFolderFileStore(
    (state) => state.setParentFolderForTargetFile,
  );
  const fetchMediaDatas = useFileStore((state) => state.fetchMediaDatas);

  useEffect(() => {
    updateSelections([]);
  }, [selectedFolderID, updateSelections]);

  // Derived state from props
  const selectable = true;
  const allowEdit = propAllowEdit;
  const allowAssign = propAllowAssign;
  const allowDelete = propAllowDelete;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10, // ✅ mouse: must move 10px before drag starts
      },
    }),
    useSensor(TouchSensor, {
      // ✅ touch: press-and-hold 200ms then drag, so a normal swipe still scrolls
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
  );

  // --- EFFECT: Sync props (mediaDatas, searchWord) on update ---
  // This combines the logic of componentDidMount/Update regarding prop changes
  useEffect(() => {
    // Only update if prop value is different, or if mediaDatas reference changes
    if (searchWord !== propSearchWord) {
      setSearchWord(propSearchWord);
    }
    if (type !== propType) {
      setType(propType);
    }
    if (currentPage !== props.currentPage) {
      setCurrentPage(props.currentPage || 1);
    }
    // Note: mediaDatas is used directly in useMemo below, avoiding a separate state sync
  }, [propSearchWord, propType, props.currentPage, mediaDatas]);

  // --- DATA PROCESSING: useMemo for filtering (Heavy Calculation) ---
  const filteredData = useMemo(() => {
    return mediaDatas
      .filter((data) => {
        let match =
          data.FileName.toLowerCase().includes(searchWord.toLowerCase()) &&
          (type ? data.FileType.toLowerCase() === type : true);

        // Date range filter
        if (currentFilters.dateRange?.length) {
          const [start, end] = currentFilters.dateRange;
          const dateToCheck = mode === "trash" ? data.DeleteDate : data.AddDate;
          const isWithin = moment(dateToCheck, "YYYY-MM-DD").isBetween(
            moment(start).startOf("day"),
            moment(end).endOf("day"),
            null,
            "[]",
          );
          match = match && isWithin;
        }

        // Usage filter
        if (currentFilters.usage?.length) {
          match = match && currentFilters.usage.includes(data.FileUseStatusID);
        }

        // Expiry filter
        if (currentFilters.expiry?.length) {
          match = match && currentFilters.expiry.includes(data.FileAvailableID);
        }

        // Branches filter
        if (
          currentFilters.branches &&
          currentFilters.branches.length > 0 &&
          !currentFilters.branches.includes("all")
        ) {
          const access = data.AccessBranches;
          if (
            access === null ||
            access === undefined ||
            access === "null" ||
            access === "all"
          ) {
            // "all" means accessible by all branches, so it matches any branch filter
            match = match && true;
          } else if (access === "") {
            // "" means not accessible at branch level, so it doesn't match specific branches
            match = false;
          } else {
            // "1,2" format
            const fileBranches = String(access)
              .split(",")
              .map((s) => s.trim());
            const hasIntersection = currentFilters.branches.some((b) =>
              fileBranches.includes(String(b)),
            );
            match = match && hasIntersection;
          }
        }

        // CreateLevel filter
        if (
          currentFilters.createLevel &&
          currentFilters.createLevel !== "all"
        ) {
          match =
            match &&
            String(data.CreateLevel) === String(currentFilters.createLevel);
        }

        return match;
      })
      .filter((data) => {
        if (!checkIsAPINew()) return true;
        // Additional filtering can be applied here if needed

        if (!data.FolderParentID && selectedFolderID === null) return true;

        return (
          data.FolderParentID === selectedFolderID ||
          (selectedFolderID === null &&
            (data.FolderParentID === null || data.FolderParentID === ""))
        );
      })
      .sort((a, b) => {
        if (sortKey && SORT_FNS[sortKey]) {
          const fn = SORT_FNS[sortKey];
          const va = fn(a),
            vb = fn(b);
          if (va < vb) return sortDir === "asc" ? -1 : 1;
          if (va > vb) return sortDir === "asc" ? 1 : -1;
          return 0;
        }
        // default: newest first by FileID
        return b.FileID - a.FileID;
      });
  }, [
    mediaDatas,
    searchWord,
    type,
    currentFilters,
    mode,
    selectedFolderID,
    sortKey,
    sortDir,
  ]);

  const currentMediaSize = filteredData.length;

  // --- DATA PROCESSING: useMemo for pagination (Heavy Calculation) ---
  const currentPageMedias = useMemo(() => {
    const start = pageSize * (currentPage - 1);
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // --- ACTIONS: Check All (Must be a useCallback since it's passed down) ---
  const checkAll = useCallback(
    (e) => {
      const isChecked = e.target.checked;
      const ids = isChecked ? currentPageMedias.map((d) => d.FileID) : [];
      // We use the simpler updateSelections here. The UI will naturally update
      // when currentSelections prop changes and triggers the render of content/headers.
      updateSelections(ids);
    },
    [currentPageMedias, updateSelections],
  );

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.xl;
  const isPhone = screens.sm === false; // < 576px — tighter padding than tablet
  const isNarrowTable = !screens.xl; // < 1200px — matches ActionsCell's isNarrow

  // --- RENDER HELPERS: Header (Must be a useCallback since it calls checkAll) ---
  const headers = useMemo(() => {
    const { selectedBrandData } = props;
    const sharingMode = selectedBrandData
      ? selectedBrandData.ContentSharingMode
      : null;
    const showOwnership =
      (sharingMode == 2 || sharingMode == "2") && mode !== "trash";
    const showBranchesAccess =
      (sharingMode == 2 || sharingMode == "2") && mode !== "trash";
    const allChecked =
      currentSelections.length > 0 &&
      currentSelections.length === currentPageMedias.length &&
      currentPageMedias.length > 0;

    const indeterminate = currentSelections.length > 0 && !allChecked;

    const SortCarets = ({ field }) => {
      const active = sortKey === field;
      const up = active && sortDir === "asc";
      const down = active && sortDir === "desc";
      const activeColor = "#6366f1";
      const inactiveColor = "#cbd5e1";
      return (
        <span
          style={{
            display: "inline-flex",
            flexDirection: "column",
            marginLeft: 4,
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 0,
              height: 0,
              borderLeft: "3.5px solid transparent",
              borderRight: "3.5px solid transparent",
              borderBottom: `4px solid ${up ? activeColor : inactiveColor}`,
            }}
          />
          <span
            style={{
              width: 0,
              height: 0,
              borderLeft: "3.5px solid transparent",
              borderRight: "3.5px solid transparent",
              borderTop: `4px solid ${down ? activeColor : inactiveColor}`,
            }}
          />
        </span>
      );
    };

    // width reserved on both sides of the label so the text itself sits
    // dead-center in the column, matching the centered data cells below —
    // otherwise the trailing sort caret pulls the label off-center.
    const SORT_CARET_WIDTH = 11;

    const SortTh = ({ field, style, children }) => {
      const isCentered = style?.justifyContent === "center";
      return (
        <div
          onClick={() => handleSort(field)}
          style={{
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            color: sortKey === field ? "#6366f1" : undefined,
            ...style,
          }}
        >
          {isCentered && (
            <span style={{ width: SORT_CARET_WIDTH, flexShrink: 0 }} />
          )}
          {children}
          <SortCarets field={field} />
        </div>
      );
    };

    return (
      <div className="media-list-header">
        {/* accent bar spacer — matches the 4px bar in rows */}
        <div style={{ width: 4, flexShrink: 0 }} />
        {selectable && (
          <div
            style={{
              flex: "0 0 100px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: 8,
            }}
          >
            <Checkbox
              indeterminate={indeterminate}
              checked={allChecked}
              onChange={checkAll}
            >
              <span style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                {t.colCheckAll}
              </span>
            </Checkbox>
          </div>
        )}
        {/* thumbnail spacer — matches width:52 + margin '0 10px 0 8px' = 70px */}
        <div style={{ width: 70, flexShrink: 0 }} />
        {/* name — takes all the leftover space so it reads comfortably;
            the meta columns stay a fixed-width group anchored to the right */}
        <SortTh field="name" style={{ flex: "1 1 260px", minWidth: 0 }}>
          {t.colMediaName}
        </SortTh>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <SortTh
            field="type"
            style={{ flex: "0 0 120px", justifyContent: "center" }}
          >
            {t.colType}
          </SortTh>
          {mode !== "trash" ? (
            <>
              <SortTh
                field="date"
                style={{ flex: "0 0 240px", justifyContent: "center" }}
              >
                {t.colActivePeriod}
              </SortTh>
              {showOwnership && (
                <div style={{ flex: "0 0 80px", textAlign: "center" }}>
                  {t.colOwnership}
                </div>
              )}
              {showBranchesAccess && (
                <div style={{ flex: "0 0 140px", textAlign: "center" }}>
                  {t.colAccess}
                </div>
              )}
              <SortTh
                field="usage"
                style={{ flex: "0 0 88px", justifyContent: "center" }}
              >
                {t.colUsage}
              </SortTh>
              <SortTh
                field="expiry"
                style={{ flex: "0 0 80px", justifyContent: "center" }}
              >
                {t.colExpiry}
              </SortTh>
            </>
          ) : (
            <>
              <SortTh
                field="deleted"
                style={{ flex: "0 0 160px", justifyContent: "center" }}
              >
                {t.colDeleteDate}
              </SortTh>
              <div style={{ flex: "0 0 160px", textAlign: "center" }}>
                {t.colRemainingDays}
              </div>
            </>
          )}
        </div>
        <div
          style={{
            flex: isNarrowTable ? "0 0 48px" : "0 0 190px",
            textAlign: "center",
          }}
        >
          {!isNarrowTable && t.colActions}
        </div>
      </div>
    );
  }, [
    currentSelections.length,
    currentPageMedias.length,
    selectable,
    mode,
    checkAll,
    props.selectedBrandData,
    props.selectedBranchData,
    isNarrowTable,
    sortKey,
    sortDir,
  ]);

  // Placeholder for Drag/Drop
  // const handleDragEnd = (result) => { /* logic */ };

  const handleDragEnd = (event) => {
    console.log("handleDragEnd");
    const { selectedBrandData, requestServerPost } = props;
    const { active, over } = event;

    if (!over) return;

    const dragged = active.data.current;
    const target = over.data.current;

    if (!dragged || !target) return;

    console.log("dragged " + JSON.stringify(dragged));
    console.log("target " + JSON.stringify(target));
    // ✅ Dragging media → dropping on folder
    if (dragged.type === "file" && target.type === "folder") {
      console.log("Move media:", dragged.name, "into folder:", target.name);

      Modal.confirm({
        title: "Move Media",
        content: `Move "${dragged.name}" into "${target.name}"?`,
        onOk: async () => {
          setParentFolderForTargetFile(
            dragged.id,
            target.id,
            () => fetchMediaDatas(selectedBrandData, props),
            () => console.log("Error"),
            props,
          );
        },
      });
    }

    if (dragged.type === "file" && target.type === "breadcrumb") {
      console.log("Move file:", dragged.name, "into folder:", target.name);

      if (dragged.parentID === target.id) return;

      Modal.confirm({
        title: "Move Meida",
        content: `Move media "${dragged.name}" up to "${target.name}"?`,
        onOk: async () => {
          setParentFolderForTargetFile(
            dragged.id,
            target.id,
            () => fetchMediaDatas(selectedBrandData, props),
            () => console.log("Error"),
            props,
          );
        },
      });
    }

    if (dragged.type === "folder" && target.type === "folder") {
      console.log("Move folder:", dragged.name, "into folder:", target.name);

      if (dragged.id === target.id) return;

      Modal.confirm({
        title: "Move Folder",
        content: `Move "${dragged.name}" into "${target.name}"?`,
        onOk: async () => {
          await setParentFolderForTarget(
            dragged.id,
            target.id,
            async () => {
              await fetchFolderFileDatas(selectedBrandData, props);
            },
            null,
            props,
          );
        },
      });
    }

    if (dragged.type === "folder" && target.type === "breadcrumb") {
      console.log("Move folder:", dragged.name, "into folder:", target.name);

      if (dragged.parentID === target.id) return;

      Modal.confirm({
        title: "Move Folder",
        content: `Move "${dragged.name}" up to "${target.name}"?`,
        onOk: async () => {
          await setParentFolderForTarget(
            dragged.id,
            target.id,
            async () => {
              await fetchFolderFileDatas(selectedBrandData, props);
            },
            null,
            props,
          );
        },
      });
    }
  };

  // --- RENDER HELPERS: Content (Media List) ---
  const content = useMemo(() => {
    if (currentMediaSize === 0) {
      const typeIconMap = {
        image: {
          icon: <ImageIcon size={28} color="#ec4899" />,
          bg: "linear-gradient(135deg,#fdf2f8,#fce7f3)",
          title: "No images yet",
        },
        video: {
          icon: <Film size={28} color="#6366f1" />,
          bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
          title: "No videos yet",
        },
        web: {
          icon: <Globe size={28} color="#14b8a6" />,
          bg: "linear-gradient(135deg,#f0fdfa,#ccfbf1)",
          title: "No web items yet",
        },
        videourl: {
          icon: <ExternalLink size={28} color="#8b5cf6" />,
          bg: "linear-gradient(135deg,#f5f3ff,#ede9fe)",
          title: "No video URLs yet",
        },
        streamingurl: {
          icon: <Radio size={28} color="#f59e0b" />,
          bg: "linear-gradient(135deg,#fffbeb,#fef3c7)",
          title: "No streaming URLs yet",
        },
        "": {
          icon: <LayoutGrid size={28} color="#6366f1" />,
          bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
          title: t.emptyMedia,
        },
      };
      const cfg = typeIconMap[type] || typeIconMap[""];
      return (
        <EmptyState
          icon={cfg.icon}
          iconBg={cfg.bg}
          title={cfg.title}
          description="Upload or add content to get started."
        />
      );
    }

    const filteredCurrentPageMedias = currentPageMedias;

    return (
      <div key={type}>
        {/* <Container groupName="folders" dragClass="dragging-media"
                    renderClone={(draggingItem) => (
                        <div
                            style={{
                                position: 'fixed',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                pointerEvents: 'none',
                            }}
                        > 
                            📁 {draggingItem?.FolderName}
                        </div>)}
                getChildPayload={(index) => filteredCurrentPageMedias[index]} >*/}
        {filteredCurrentPageMedias.map((data) => (
          // <Draggable key={`file_${data.FileID}`}>
          <DraggableWrapper
            id={`file_${data.FileID}`}
            data={{
              type: "file",
              id: data.FileID,
              name: data.FileName,
              parentID: data.FolderParentID,
            }}
          >
            <MediaListElement
              key={data.FileID}
              mode={mode}
              branches={props.branches}
              selectedBrandData={props.selectedBrandData}
              selectedBranchData={props.selectedBranchData}
              selectable
              isSelected={currentSelections.includes(data.FileID)}
              onSelectionChanged={(e) =>
                // updateSelection does not need renderMedias callback anymore,
                // as the new currentSelections prop will trigger component re-render.
                updateSelection(data.FileID, e.target.checked)
              }
              allowEdit={allowEdit}
              allowAssign={allowAssign}
              allowDelete={allowDelete}
              allowRestore={mode === "trash"}
              data={data}
              onDeleteClick={() => onDelete(data)}
              onReplaceClick={() => onReplace(data)}
              onPreviewClick={() => onPreview(data)}
              onRestoreClick={() => onRestore(data)}
              onEditClick={() => onEdit(data)}
              onAssignClick={() => onAssign(data)}
            />
          </DraggableWrapper>
        ))}
        {/* </Container> */}
      </div>
    );
  }, [
    currentPageMedias,
    currentSelections,
    mode,
    type,
    allowEdit,
    allowAssign,
    allowDelete,
    props.selectedBrandData,
    props.selectedBranchData,
    // Stable props
    updateSelection,
    onDelete,
    onReplace,
    onPreview,
    onRestore,
    onEdit,
    onAssign,
  ]);

  // --- VIEW MODE HANDLER ---
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem("media-view-mode", mode);
  }, []);

  // --- RENDER HELPERS: Grid Content ---
  const gridContent = useMemo(() => {
    if (currentMediaSize === 0) {
      const typeIconMap = {
        image: {
          icon: <ImageIcon size={28} color="#ec4899" />,
          bg: "linear-gradient(135deg,#fdf2f8,#fce7f3)",
          title: "No images yet",
        },
        video: {
          icon: <Film size={28} color="#6366f1" />,
          bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
          title: "No videos yet",
        },
        web: {
          icon: <Globe size={28} color="#14b8a6" />,
          bg: "linear-gradient(135deg,#f0fdfa,#ccfbf1)",
          title: "No web items yet",
        },
        videourl: {
          icon: <ExternalLink size={28} color="#8b5cf6" />,
          bg: "linear-gradient(135deg,#f5f3ff,#ede9fe)",
          title: "No video URLs yet",
        },
        streamingurl: {
          icon: <Radio size={28} color="#f59e0b" />,
          bg: "linear-gradient(135deg,#fffbeb,#fef3c7)",
          title: "No streaming URLs yet",
        },
        "": {
          icon: <LayoutGrid size={28} color="#6366f1" />,
          bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
          title: t.emptyMedia,
        },
      };
      const cfg = typeIconMap[type] || typeIconMap[""];
      return (
        <EmptyState
          icon={cfg.icon}
          iconBg={cfg.bg}
          title={cfg.title}
          description="Upload or add content to get started."
        />
      );
    }

    const getBadgeClass = (fileType) => {
      const t = fileType.toLowerCase();
      if (t === "image") return "image";
      if (t === "video" || t === "videourl") return "video";
      return "other";
    };

    const getBadgeLabel = (fileType) => {
      switch (fileType.toLowerCase()) {
        case "image":
          return "IMG";
        case "video":
          return "VID";
        case "web":
          return "WEB";
        case "videourl":
          return "URL";
        case "streamingurl":
          return "LIVE";
        default:
          return fileType.toUpperCase().slice(0, 4);
      }
    };

    return (
      <div className="media-grid-view">
        {currentPageMedias.map((data) => {
          const fileType = data.FileType.toLowerCase();
          const isImage = fileType === "image";

          return (
            <div key={data.FileID} className="media-grid-item">
              <div className="media-grid-thumb">
                {isImage ? (
                  <img
                    src={data.UploadFile}
                    alt={data.FileName}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "/cms/assets/images/placeholders/image_placeholder.png";
                    }}
                  />
                ) : (
                  <div className="media-grid-thumb-icon">
                    {getMediaTypeIcon(fileType, 32)}
                  </div>
                )}
                <span
                  className={`media-grid-badge ${getBadgeClass(data.FileType)}`}
                >
                  {getBadgeLabel(data.FileType)}
                </span>
                <div className="media-grid-overlay">
                  <Tooltip title="Preview">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      style={{ color: "#fff" }}
                      onClick={() => onPreview(data)}
                    />
                  </Tooltip>
                  {allowEdit && (
                    <Tooltip title="Edit">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ color: "#fff" }}
                        onClick={() => onEdit(data)}
                      />
                    </Tooltip>
                  )}
                  {allowDelete && (
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        style={{ color: "#fff" }}
                        onClick={() => onDelete(data)}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="media-grid-info">
                <div className="media-grid-name" title={data.FileName}>
                  {data.FileName}
                </div>
                <div className="media-grid-meta">
                  {data.FileType} · {data.AddDate}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [
    currentPageMedias,
    type,
    allowEdit,
    allowDelete,
    onPreview,
    onEdit,
    onDelete,
    currentMediaSize,
  ]);

  // --- PAGINATION HANDLERS (Must be a useCallback since they're passed down) ---
  const handleFilterChanged = useCallback(
    (filters) => {
      setCurrentFilters(filters);
      setCurrentPage(1); // Reset page on filter change
      updateSelections([]); // Clear selections
    },
    [updateSelections],
  );

  const handlePaginationChange = useCallback(
    (newPage) => {
      updateSelections([]);
      setCurrentPage(newPage);
    },
    [updateSelections],
  );

  const handlePageSizeChange = useCallback((current, size) => {
    setPageSize(size);
  }, []);

  const showFolders = checkIsAPINew() && mode !== "trash";

  // Folder card collapses via its header (tablet/mobile only — see the
  // title render below) but starts expanded by default; the user taps
  // the header to collapse it themselves when they want the extra space.
  const [folderCollapsed, setFolderCollapsed] = useState(false);

  // On mobile, the Folders card (+ its pagination) stacks above the Media
  // Files card instead of sitting beside it. Both stay sticky, so the Media
  // Files header's sticky offset is measured off the folder stack's actual
  // rendered height rather than a hardcoded number — keeps them stacked
  // cleanly with no overlap regardless of folder count or text length.
  const folderStackRef = useRef(null);
  const [folderStackHeight, setFolderStackHeight] = useState(0);

  useEffect(() => {
    if (!isMobile || !showFolders) return;
    const el = folderStackRef.current;
    if (!el) return;
    const update = () => setFolderStackHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, showFolders]);

  // --- DATA PROCESSING: Folder filtering ---
  const filteredFolders = useMemo(() => {
    if (!folderFileDatas) return [];
    let folders = folderFileDatas;

    const { selectedBrandData, selectedBranchData } = props;
    console.log(
      "[MediaLists] folders raw:",
      folderFileDatas.map((f) => ({
        FolderID: f.FolderID,
        FolderName: f.FolderName,
        AccessBranches: f.AccessBranches,
        CreateLevel: f.CreateLevel,
      })),
    );
    console.log(
      "[MediaLists] selectedBranchData:",
      selectedBranchData?.BranchID ?? null,
      "| ContentSharingMode:",
      selectedBrandData?.ContentSharingMode ?? null,
    );

    // At branch level with sharingMode 2, hide folders inaccessible to this branch
    const sharingMode = selectedBrandData
      ? selectedBrandData.ContentSharingMode
      : null;
    if ((sharingMode == 2 || sharingMode == "2") && selectedBranchData) {
      const branchId = String(selectedBranchData.BranchID);
      folders = folders.filter((folder) => {
        const access = folder.AccessBranches;
        if (!access || access === "null" || access === "all") return true;
        if (access === "") return false;
        return String(access)
          .split(",")
          .map((s) => s.trim())
          .includes(branchId);
      });
    }

    if (
      currentFilters.branches &&
      currentFilters.branches.length > 0 &&
      !currentFilters.branches.includes("all")
    ) {
      folders = folders.filter((folder) => {
        const access = folder.AccessBranches;
        if (
          access === null ||
          access === undefined ||
          access === "null" ||
          access === "all"
        ) {
          return true;
        } else if (access === "") {
          return false;
        } else {
          const folderBranches = String(access)
            .split(",")
            .map((s) => s.trim());
          return currentFilters.branches.some((b) =>
            folderBranches.includes(String(b)),
          );
        }
      });
    }

    if (currentFilters.createLevel && currentFilters.createLevel !== "all") {
      folders = folders.filter(
        (folder) =>
          String(folder.CreateLevel) === String(currentFilters.createLevel),
      );
    }

    return folders;
  }, [
    folderFileDatas,
    currentFilters.branches,
    currentFilters.createLevel,
    props.selectedBranchData,
    props.selectedBrandData,
  ]);

  // Media Files card header sticks right below the folder stack (when
  // present) instead of a fixed offset, so the two sticky blocks never
  // overlap — and sit flush with no gap — regardless of folder count.
  const mediaHeaderStickyTop = !isMobile
    ? stickyOffset
    : showFolders
      ? stickyOffset + folderStackHeight
      : stickyOffset;

  // --- RENDER ---
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragEnd={handleDragEnd}
    >
      <div style={{ position: "relative" }}>
        {/* Filter bar — standalone row */}
        <div className="media-filter-row">
          <MediaFilter
            mode={mode}
            onFilterChange={handleFilterChanged}
            branches={branches}
          />
        </div>

        {/* Pre-card row: breadcrumb (left, with folders) + pagination (right, with media) */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isPhone ? 10 : 20,
            alignItems: isMobile ? "stretch" : "flex-end",
            marginBottom: 8,
          }}
        >
          {showFolders && (
            <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0 }}>
              <FolderBreadcrumb />
            </div>
          )}
          {/* On mobile, when folders are shown, the pagination moves below
              the Folders card instead (see Cards row) so it doesn't sit
              between the breadcrumb and the Folders card. */}
          {!(isMobile && showFolders) && (
            <div style={{ flex: 1 }}>
              <div className="media-pagination-row" style={{ padding: 0 }}>
                <Pagination
                  total={currentMediaSize}
                  pageSize={pageSize}
                  current={currentPage}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePaginationChange}
                  onShowSizeChange={handlePageSizeChange}
                />
              </div>
            </div>
          )}
        </div>

        {/* Cards row */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isPhone ? 10 : 20,
            alignItems: "flex-start",
          }}
        >
          {showFolders && (
            <div
              ref={folderStackRef}
              style={{
                width: isMobile ? "100%" : 220,
                flexShrink: 0,
                // On mobile the folder stack (card + its pagination) and the
                // Media Files card stack in one column instead of sitting
                // side by side. Both stay sticky; the Media Files header's
                // offset is measured off this block's real height so they
                // dock directly beneath each other with no overlap.
                position: "sticky",
                top: stickyOffset,
                zIndex: 20,
                background: isMobile ? "#fff" : undefined,
              }}
            >
              <Card
                className="folders-card"
                title={
                  // Accordion (clickable header, collapsible body) is a
                  // tablet/mobile space-saving affordance only — desktop
                  // has room to always show the folder list, so its title
                  // stays plain, non-interactive text.
                  isMobile ? (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setFolderCollapsed((c) => !c)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setFolderCollapsed((c) => !c);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        userSelect: "none",
                        margin: "0 -24px",
                        padding: "0 24px",
                      }}
                    >
                      <span>{t.labelFolders}</span>
                      {folderCollapsed ? (
                        <ChevronDown size={16} color="#94a3b8" />
                      ) : (
                        <ChevronUp size={16} color="#94a3b8" />
                      )}
                    </div>
                  ) : (
                    t.labelFolders
                  )
                }
                style={{
                  // No marginBottom here — the Folders card is now the
                  // sole child of its sticky wrapper (pagination moved
                  // inside it), and a flex item establishes its own
                  // block-formatting context, so a trailing child margin
                  // doesn't collapse out of the wrapper — it gets trapped
                  // inside it as extra background-colored space. Spacing
                  // from the Media Files column is handled by the Cards
                  // row's own `gap` instead.
                  backgroundColor: isMobile
                    ? "#fff"
                    : "rgba(255, 255, 255, 0.65)",
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: 0 }}
              >
                {(!isMobile || !folderCollapsed) && (
                  <div style={{ padding: "8px 0" }}>
                    <FolderList
                      folders={filteredFolders}
                      onSelect={(folder) => {
                        /* logic */
                      }}
                      compact={isMobile}
                      selectedBrandData={props.selectedBrandData}
                    />
                  </div>
                )}
                {/* Page numbers for the Media Files list live inside the
                    Folders card on mobile (rather than as a separate block
                    below it), so they stay reachable even while the
                    folder list itself is collapsed. */}
                {isMobile && (
                  <div
                    className="media-pagination-row"
                    style={{
                      padding: "10px 16px",
                      borderTop: folderCollapsed
                        ? "none"
                        : "1px solid #f1f5f9",
                    }}
                  >
                    <Pagination
                      total={currentMediaSize}
                      pageSize={pageSize}
                      current={currentPage}
                      showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`
                      }
                      onChange={handlePaginationChange}
                      onShowSizeChange={handlePageSizeChange}
                    />
                  </div>
                )}
              </Card>
            </div>
          )}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              width: isMobile ? "100%" : undefined,
            }}
          >
            {/* Media List */}
            <Card
              className="media-files-card"
              title={
                <span
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  {t.labelMediaFiles}
                  {currentMediaSize > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 20,
                        background: "#eef2ff",
                        color: "#4f46e5",
                        border: "1px solid #c7d2fe",
                      }}
                    >
                      {currentMediaSize}
                    </span>
                  )}
                </span>
              }
              style={{ overflow: "visible" }}
              bodyStyle={{ padding: 0 }}
              headStyle={{
                position: "sticky",
                top: mediaHeaderStickyTop,
                zIndex: 19,
                background: "#fff",
                borderRadius: "2px 2px 0 0",
              }}
              extra={
                <div className="media-view-toggle">
                  <Tooltip title="List view">
                    <button
                      className={`media-view-toggle-btn${viewMode === "list" ? " active" : ""}`}
                      onClick={() => handleViewModeChange("list")}
                    >
                      <ListIcon size={15} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Grid view">
                    <button
                      className={`media-view-toggle-btn${viewMode === "grid" ? " active" : ""}`}
                      onClick={() => handleViewModeChange("grid")}
                    >
                      <LayoutGrid size={15} />
                    </button>
                  </Tooltip>
                </div>
              }
            >
              {viewMode === "list" ? (
                <div style={{ overflowX: "auto" }}>
                  <div
                    style={{
                      minWidth: isMobile ? 0 : 750,
                      padding: isPhone ? 12 : 24,
                    }}
                  >
                    {headers}
                    {content}
                  </div>
                </div>
              ) : (
                gridContent
              )}
            </Card>
            {/* Bottom pagination */}
            <div className="media-pagination-row">
              <Pagination
                total={currentMediaSize}
                pageSize={pageSize}
                current={currentPage}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                onChange={handlePaginationChange}
                onShowSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
};

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

export default connect(mapStateToProps, {
  requestServerPost,
  requestServerGet,
})(MediaLists);

///
// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import moment from "moment";
// import { Checkbox, Pagination, Empty, Card, Row, Col, Button } from "antd";
// import { RollbackOutlined } from "@ant-design/icons";
// import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
// import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
// import MediaListElement from "../../components/MediaListElement";
// import MediaFilter from "../../components/views/MediaFilter";
// import FolderList from "./FolderList";
// import FolderBreadcrumb from "./FolderBreadcrumb";
// import useFolderFileStore from "../../store/folderFileStore";

// const MediaLists = (props) => {
//   const {
//     mediaDatas = [],
//     type: propType = "",
//     allowEdit = true,
//     allowAssign = true,
//     allowDelete = true,
//     searchWord: propSearchWord = "",
//     mode = "media",
//     currentSelections = [],
//     onPreview = () => {},
//     onEdit = () => {},
//     onDelete = () => {},
//     onReplace = () => {},
//     onRestore = () => {},
//     onAssign = () => {},
//     updateSelection = () => {},
//     updateSelections = () => {},
//   } = props;

//   const [currentPage, setCurrentPage] = useState(props.currentPage || 1);
//   const [pageSize, setPageSize] = useState(10);
//   const [currentFilters, setCurrentFilters] = useState({});
//   const [searchWord, setSearchWord] = useState(propSearchWord);
//   const [type, setType] = useState(propType);
//   const [activeDragItem, setActiveDragItem] = useState(null);

//   const folderFileDatas = useFolderFileStore((state) => state.folderFileDatas);
//   const selectParentFolder = useFolderFileStore((state) => state.selectParentFolder);
//   const selectedFolderID = useFolderFileStore((state) => state.selectedFolderID);

//   useEffect(() => {
//     if (searchWord !== propSearchWord) setSearchWord(propSearchWord);
//     if (type !== propType) setType(propType);
//     if (currentPage !== props.currentPage) setCurrentPage(props.currentPage || 1);
//   }, [propSearchWord, propType, props.currentPage, mediaDatas]);

//   const filteredData = useMemo(() => {
//     return mediaDatas
//       .filter((data) => {
//         let match =
//           data.FileName.toLowerCase().includes(searchWord.toLowerCase()) &&
//           (type ? data.FileType.toLowerCase() === type : true);

//         if (currentFilters.dateRange?.length) {
//           const [start, end] = currentFilters.dateRange;
//           const dateToCheck = mode === "trash" ? data.DeleteDate : data.AddDate;
//           const isWithin = moment(dateToCheck, "YYYY-MM-DD").isBetween(
//             moment(start).startOf("day"),
//             moment(end).endOf("day"),
//             null,
//             "[]"
//           );
//           match = match && isWithin;
//         }

//         if (currentFilters.usage?.length) {
//           match = match && currentFilters.usage.includes(data.FileUseStatusID);
//         }

//         if (currentFilters.expiry?.length) {
//           match = match && currentFilters.expiry.includes(data.FileAvailableID);
//         }

//         return match;
//       })
//       .sort((a, b) => b.FileID - a.FileID);
//   }, [mediaDatas, searchWord, type, currentFilters, mode]);

//   const currentMediaSize = filteredData.length;

//   const currentPageMedias = useMemo(() => {
//     const start = pageSize * (currentPage - 1);
//     return filteredData.slice(start, start + pageSize);
//   }, [filteredData, currentPage, pageSize]);

//   const checkAll = useCallback(
//     (e) => {
//       const isChecked = e.target.checked;
//       const ids = isChecked ? currentPageMedias.map((d) => d.FileID) : [];
//       updateSelections(ids);
//     },
//     [currentPageMedias, updateSelections]
//   );

//   const headers = useMemo(() => {
//     const allChecked =
//       currentSelections.length > 0 &&
//       currentSelections.length === currentPageMedias.length &&
//       currentPageMedias.length > 0;

//     const indeterminate = currentSelections.length > 0 && !allChecked;

//     return (
//       <div className="media-list-header">
//         <div style={{ flex: "0.3" }} className="center-content">
//           <Checkbox indeterminate={indeterminate} checked={allChecked} onChange={checkAll}>
//             Check all
//           </Checkbox>
//         </div>
//         <div style={{ width: "200px" }}></div>
//         <div style={{ flex: 0.8 }} className="center-content">
//           Media Name
//         </div>
//         <div style={{ flex: 0.5 }} className="center-content">
//           Type
//         </div>
//         {mode !== "trash" ? (
//           <>
//             <div style={{ flex: 0.8 }} className="center-content">
//               Active Period
//             </div>
//             <div style={{ flex: 0.2 }} className="center-content">
//               Usage
//             </div>
//             <div style={{ flex: 0.2 }} className="center-content">
//               Expiry
//             </div>
//           </>
//         ) : (
//           <>
//             <div style={{ flex: 0.4 }} className="center-content">
//               Delete Date
//             </div>
//             <div style={{ flex: 0.4 }} className="center-content">
//               Remaining Days
//             </div>
//           </>
//         )}
//         <div style={{ flex: 0.5 }} className="center-content">
//           Actions
//         </div>
//       </div>
//     );
//   }, [currentSelections.length, currentPageMedias.length, mode, checkAll]);

//   // DnD Sensors (optimized for mouse only)
//   const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

//   const handleDragStart = (event) => {
//     const draggedItem = currentPageMedias.find((m) => m.FileID === event.active.id);
//     setActiveDragItem(draggedItem);
//   };

//   const handleDragEnd = (event) => {
//     const { over, active } = event;
//     setActiveDragItem(null);

//     if (over && over.data?.current?.folder) {
//       const targetFolder = over.data.current.folder;
//       const draggedMedia = currentPageMedias.find((m) => m.FileID === active.id);
//       console.log("Dropped:", draggedMedia.FileName, "into folder:", targetFolder.FolderName);
//       // handle move media into folder here
//     }
//   };

//   const filteredCurrentPageMedias = useMemo(() => {
//     return currentPageMedias.filter(
//       (data) =>
//         data.FolderParentID === selectedFolderID ||
//         (selectedFolderID === null && (data.FolderParentID === null || data.FolderParentID === ""))
//     );
//   }, [currentPageMedias, selectedFolderID]);

//   const content = useMemo(() => {
//     if (currentMediaSize === 0) return <Empty />;

//     return (
//       <SortableContext
//         items={filteredCurrentPageMedias.map((d) => d.FileID)}
//         strategy={verticalListSortingStrategy}
//       >
//         {filteredCurrentPageMedias.map((data) => (
//           <MediaListElement
//             key={data.FileID}
//             id={data.FileID}
//             mode={mode}
//             selectable
//             isSelected={currentSelections.includes(data.FileID)}
//             onSelectionChanged={(e) => updateSelection(data.FileID, e.target.checked)}
//             allowEdit={allowEdit}
//             allowAssign={allowAssign}
//             allowDelete={allowDelete}
//             allowRestore={mode === "trash"}
//             data={data}
//             onDeleteClick={() => onDelete(data)}
//             onReplaceClick={() => onReplace(data)}
//             onPreviewClick={() => onPreview(data)}
//             onRestoreClick={() => onRestore(data)}
//             onEditClick={() => onEdit(data)}
//             onAssignClick={() => onAssign(data)}
//           />
//         ))}
//       </SortableContext>
//     );
//   }, [
//     filteredCurrentPageMedias,
//     currentSelections,
//     mode,
//     allowEdit,
//     allowAssign,
//     allowDelete,
//     updateSelection,
//     onDelete,
//     onReplace,
//     onPreview,
//     onRestore,
//     onEdit,
//     onAssign,
//   ]);

//   const handleFilterChanged = useCallback(
//     (filters) => {
//       setCurrentFilters(filters);
//       setCurrentPage(1);
//       updateSelections([]);
//     },
//     [updateSelections]
//   );

//   const handlePaginationChange = useCallback(
//     (newPage) => {
//       updateSelections([]);
//       setCurrentPage(newPage);
//     },
//     [updateSelections]
//   );

//   const handlePageSizeChange = useCallback((current, size) => setPageSize(size), []);

//   return (
//     <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
//       <div style={{ position: "relative" }}>
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "end",
//             width: "100%",
//             padding: "20px 0",
//           }}
//         >
//           <MediaFilter mode={mode} onFilterChange={handleFilterChanged} />
//           <Pagination
//             total={currentMediaSize}
//             pageSize={pageSize}
//             current={currentPage}
//             style={{ marginBottom: "20px", float: "right", minWidth: "650px", display: "flex", justifyContent: "end" }}
//             showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
//             onChange={handlePaginationChange}
//             onShowSizeChange={handlePageSizeChange}
//           />
//         </div>

//         <div style={{ marginBottom: "16px" }}>
//           <FolderBreadcrumb
//             path={[{ id: 0, name: "Root" }, { id: 2, name: "Folder0" }]}
//             onNavigate={(folder, index) => {}}
//           />
//         </div>

//         <Row style={{ width: "100%", gap: "20px" }}>
//           <Col span={24} xxl={3}>
//             <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
//               <Card
//                 title="Folders"
//                 extra={
//                   selectedFolderID ? (
//                     <Button
//                       onClick={selectParentFolder}
//                       type="link"
//                       icon={<RollbackOutlined style={{ fontSize: "22px" }} />}
//                     />
//                   ) : undefined
//                 }
//                 style={{ marginBottom: "16px", backgroundColor: "rgba(255,255,255,0.65)" }}
//               >
//                 <FolderList folders={folderFileDatas} onSelect={(folder) => {}} />
//               </Card>
//             </div>
//           </Col>

//           <Col flex="auto">
//             <Card title="Media Files">
//               {headers}
//               {content}
//             </Card>
//           </Col>
//         </Row>

//         <Pagination
//           total={currentMediaSize}
//           pageSize={pageSize}
//           current={currentPage}
//           style={{ marginTop: "20px", float: "right" }}
//           showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
//           onChange={handlePaginationChange}
//           onShowSizeChange={handlePageSizeChange}
//         />
//       </div>

//       {/* Drag Preview */}
//       <DragOverlay>
//         {activeDragItem ? (
//           <div style={{ opacity: 0.8 }}>
//             <MediaListElement data={activeDragItem} isPreview />
//           </div>
//         ) : null}
//       </DragOverlay>
//     </DndContext>
//   );
// };

// export default MediaLists;
